import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api'
import { useToast } from '../components/Toast'
import { useAsyncData } from '../hooks/useAsyncData'
import RefreshButton from '../components/RefreshButton'
import Spinner, { PageSpinner } from '../components/Spinner'
import CapabilityPicker from '../components/CapabilityPicker'
import {
  ACCOUNT_TYPE_COLORS, ACCOUNT_TYPE_LABELS, ACCOUNT_TYPES,
  BUNDLE_CATEGORIES, CATEGORY_MODELS, ENVIRONMENTS, FEDERATIONS,
} from '../constants'
import { getUser } from '../auth'
import {
  emptyAccount, emptyDevice, computeUnmet, buildBundlePayload,
  emptyExternal, computeUnmetExternal, buildExternalPayload,
} from './bundleForm'
import Instant from '../components/Instant'

const STATUS_BADGE = {
  pending: 'badge-warning',
  associated: 'badge-success',
  disassociation_failed: 'badge-error',
}

// Device spec rows (category / model / connection / virtual / count / capabilities), shared
// by the inventory-account and own-account forms.
function DeviceSpecList({ devices, catalog, optional, onAdd, onRemove, onDevice }) {
  return (
  <div className="flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <p className="text-xs text-base-content/50">{optional ? 'Devices (optional)' : 'Devices (at least one)'}</p>
      <button type="button" className="btn btn-xs btn-outline" onClick={onAdd}>
        + Add device
      </button>
    </div>

    {devices.length === 0 && (
      <p className="text-xs text-base-content/30">No devices added yet.</p>
    )}

    {devices.map((device, di) => {
      const models = CATEGORY_MODELS[device.category] ?? []
      const hasModels = models.length > 1
      const caps = device.capabilities ?? []
      return (
        <div key={device.id} className="flex flex-col gap-2 rounded-md border border-base-300/60 p-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="badge badge-secondary badge-sm shrink-0 whitespace-nowrap">device</span>
            <select
              className="select select-bordered select-sm"
              aria-label="Device category"
              value={device.category}
              onChange={e => onDevice(di, 'category', e.target.value)}
            >
              {BUNDLE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {hasModels && (
              <select
                className="select select-bordered select-sm"
                aria-label="Device model"
                value={device.model ?? ''}
                onChange={e => onDevice(di, 'model', e.target.value || null)}
              >
                <option value="">Any model</option>
                {models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            <select
              className="select select-bordered select-sm"
              aria-label="Device connection"
              title="Connection to Emporia Cloud when the bundle is created"
              value={device.connection ?? 'online'}
              onChange={e => onDevice(di, 'connection', e.target.value)}
            >
              <option value="online">Online only</option>
              <option value="any">Any connection</option>
              <option value="offline">Offline only</option>
            </select>
            <select
              className="select select-bordered select-sm"
              aria-label="Virtual or physical device"
              title="Virtual devices are mockserver-simulated and can be driven into states; physical is real hardware (firmware updates, Bluetooth onboarding, real power)"
              value={device.is_virtual ?? ''}
              onChange={e => onDevice(di, 'is_virtual', e.target.value)}
            >
              <option value="">Virtual + physical</option>
              <option value="true">Virtual only</option>
              <option value="false">Physical only</option>
            </select>
            <input
              type="number"
              min="1"
              aria-label="Device count"
              value={device.count}
              onChange={e => onDevice(di, 'count', Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="input input-bordered input-sm w-16 text-center ml-auto"
            />
            <button
              type="button"
              className="btn btn-xs btn-ghost btn-square text-error"
              onClick={() => onRemove(di)}
              aria-label="Remove device"
              title="Remove device"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap pl-1">
            <CapabilityPicker
              selected={caps}
              catalog={catalog}
              onChange={next => onDevice(di, 'capabilities', next)}
              size="xs"
            />
            {caps.map(name => {
              const cap = catalog.find(c => c.name === name)
              return (
                <span key={name} className="badge badge-ghost badge-sm">
                  {cap ? cap.label : name}
                </span>
              )
            })}
            {caps.length === 0 && <span className="text-xs opacity-40">any capability</span>}
          </div>
        </div>
      )
    })}
  </div>
  )
}

function AccountBlock({ index, account, catalog, canRemove, onRemove, onEnv, onType, onToggleFed, onTogglePublicCharger, onToggleInternalRing, onAddDevice, onRemoveDevice, onDevice }) {
  const isPartner = account.account_type === 'PARTNER'
  return (
    <div className="rounded-box border border-base-300 p-5 flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="badge badge-primary shrink-0 whitespace-nowrap">
          account {index + 1}
        </span>
        <select
          className="select select-bordered select-sm"
          aria-label="Environment"
          value={account.environment}
          onChange={e => onEnv(e.target.value)}
        >
          {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
        </select>
        <select
          className="select select-bordered select-sm"
          aria-label="Account type"
          value={account.account_type}
          onChange={e => onType(e.target.value)}
        >
          {ACCOUNT_TYPES.map(type => (
            <option key={type} value={type}>{ACCOUNT_TYPE_LABELS[type]}</option>
          ))}
        </select>
        {canRemove && (
          <button
            type="button"
            className="btn btn-xs btn-ghost btn-square ml-auto text-error"
            onClick={onRemove}
            aria-label="Remove account"
            title="Remove account"
          >
            ✕
          </button>
        )}
      </div>

      {/* The charger isn't inventory-tracked — it comes with the partner login, so
          this only narrows which partner account gets claimed. */}
      {isPartner && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!account.has_public_charger}
            onChange={onTogglePublicCharger}
          />
          <span className="text-sm">Must have a public charger</span>
        </label>
      )}

      {/* The PostHog "QA internal ring" cohort: flags still in their internal stage
          target it, so they read as on for this account until the bundle ends. */}
      {!isPartner && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!account.internal_ring}
            onChange={onToggleInternalRing}
          />
          <span className="text-sm">In the PostHog internal ring</span>
          <span className="text-xs text-base-content/40">
            (internal-stage flags on; the account must have signed in to the app once)
          </span>
        </label>
      )}

      {/* Federations select among customer accounts by identity provider. Partner
          accounts live in a separate Cognito pool and have none. */}
      {isPartner ? (
        <p className="text-xs text-base-content/40">
          Partner accounts sign in through the partner portal, so there are no federations to pick.
        </p>
      ) : (
        <div>
          <p className="text-xs text-base-content/50 mb-2">Required federations (select at least one)</p>
          <div className="flex flex-wrap gap-4">
            {FEDERATIONS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={account.federations.includes(key)}
                  onChange={() => onToggleFed(key)}
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="divider my-0" />

      <DeviceSpecList
        devices={account.devices}
        catalog={catalog}
        optional
        onAdd={onAddDevice}
        onRemove={onRemoveDevice}
        onDevice={onDevice}
      />
    </div>
  )
}

// Models depend on the category, so a category change resets the model.
function updateDeviceRow(devices, di, field, value) {
  return devices.map((d, x) => {
    if (x !== di) return d
    const next = { ...d, [field]: value }
    if (field === 'category') next.model = null
    return next
  })
}

// Own-account bundle: devices go onto any Emporia account email — typically your
// own work login — that the inventory doesn't track. Only devices are reserved.
function ExternalBlock({ ext, catalog, onField, onAddDevice, onRemoveDevice, onDevice }) {
  return (
    <div className="rounded-box border border-base-300 p-5 flex flex-col gap-4">
      <label className="flex flex-col gap-1">
        <span className="text-xs text-base-content/50">Emporia account email</span>
        <input
          type="email"
          className="input input-bordered input-sm w-full"
          aria-label="Account email"
          placeholder="you@emporiaenergy.com"
          autoComplete="email"
          value={ext.email}
          onChange={e => onField('email', e.target.value)}
        />
      </label>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          className="select select-bordered select-sm"
          aria-label="Environment"
          value={ext.environment}
          onChange={e => onField('environment', e.target.value)}
        >
          {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
        </select>
        <select
          className="select select-bordered select-sm"
          aria-label="Account type"
          value={ext.account_type}
          onChange={e => onField('account_type', e.target.value)}
        >
          {ACCOUNT_TYPES.map(type => (
            <option key={type} value={type}>{ACCOUNT_TYPE_LABELS[type]}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-base-content/40">
        The account must already exist in Emporia Cloud for this environment. Devices are
        associated to it and removed again when you cancel the bundle.
      </p>

      <div className="divider my-0" />

      <DeviceSpecList
        devices={ext.devices}
        catalog={catalog}
        onAdd={onAddDevice}
        onRemove={onRemoveDevice}
        onDevice={onDevice}
      />
    </div>
  )
}

function CreateBundle({ onCreated }) {
  // 'inventory' = QA inventory accounts (POST /bundle); 'external' = your own
  // Emporia account (POST /bundle/external). Each keeps its own form state.
  const [mode, setMode] = useState('inventory')
  const [accounts, setAccounts] = useState(() => [emptyAccount()])
  const [ext, setExt] = useState(() => emptyExternal(getUser()?.email ?? ''))
  const [loading, setLoading] = useState(false)
  const [capCatalog, setCapCatalog] = useState([])
  const { show } = useToast()

  useEffect(() => {
    api.getCapabilities().then(setCapCatalog).catch(() => { /* pickers just stay empty */ })
  }, [])

  function addAccount() {
    setAccounts(prev => [...prev, emptyAccount()])
  }

  function removeAccount(i) {
    setAccounts(prev => prev.filter((_, idx) => idx !== i))
  }

  function setEnv(i, env) {
    setAccounts(prev => prev.map((a, idx) => idx === i ? { ...a, environment: env } : a))
  }

  function setType(i, account_type) {
    setAccounts(prev => prev.map((a, idx) => {
      if (idx !== i) return a
      // Switching to PARTNER drops any picked federations (the API rejects them);
      // switching back restores the default so the form isn't left unsubmittable.
      const federations = account_type === 'PARTNER'
        ? []
        : (a.federations.length ? a.federations : ['emporia'])
      // The flag is partner-only, so switching to CUSTOMER clears it.
      const has_public_charger =
        account_type === 'PARTNER' ? a.has_public_charger : false
      // The ring is customer-only, so switching to PARTNER clears it.
      const internal_ring = account_type === 'PARTNER' ? false : a.internal_ring
      return { ...a, account_type, federations, has_public_charger, internal_ring }
    }))
  }

  function togglePublicCharger(i) {
    setAccounts(prev => prev.map((a, idx) =>
      idx === i ? { ...a, has_public_charger: !a.has_public_charger } : a
    ))
  }

  function toggleInternalRing(i) {
    setAccounts(prev => prev.map((a, idx) =>
      idx === i ? { ...a, internal_ring: !a.internal_ring } : a
    ))
  }

  function toggleFed(i, key) {
    setAccounts(prev => prev.map((a, idx) => {
      if (idx !== i) return a
      const federations = a.federations.includes(key)
        ? a.federations.filter(f => f !== key)
        : [...a.federations, key]
      return { ...a, federations }
    }))
  }

  function addDevice(i) {
    setAccounts(prev => prev.map((a, idx) => idx === i ? { ...a, devices: [...a.devices, emptyDevice()] } : a))
  }

  function removeDevice(i, di) {
    setAccounts(prev => prev.map((a, idx) => idx === i ? { ...a, devices: a.devices.filter((_, x) => x !== di) } : a))
  }

  function setDevice(i, di, field, value) {
    setAccounts(prev => prev.map((a, idx) =>
      idx === i ? { ...a, devices: updateDeviceRow(a.devices, di, field, value) } : a
    ))
  }

  const unmet = mode === 'external' ? computeUnmetExternal(ext) : computeUnmet(accounts)

  async function handleSubmit(e) {
    e.preventDefault()
    if (unmet.length > 0) {
      show(unmet[0], 'error')
      return
    }
    setLoading(true)
    try {
      if (mode === 'external') {
        const bundle = await api.createExternalBundle(buildExternalPayload(ext))
        const deviceCount = bundle.accounts.reduce((s, acc) => s + acc.device_mdis.length, 0)
        show(`Bundle reserved — ${deviceCount} device(s) on ${ext.email.trim()}`, 'success')
        // Keep the email/environment/type for the next one; reset the devices.
        setExt(prev => ({ ...prev, devices: [emptyDevice()] }))
      } else {
        const bundle = await api.createBundle(buildBundlePayload(accounts))
        const deviceCount = bundle.accounts.reduce((s, acc) => s + acc.device_mdis.length, 0)
        show(`Bundle reserved — ${bundle.accounts.length} account(s) + ${deviceCount} device(s)`, 'success')
        setAccounts([emptyAccount()])
      }
      onCreated()
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 max-w-xl">
      <div role="tablist" aria-label="Bundle target" className="tabs tabs-box tabs-sm self-start">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'inventory'}
          className={`tab ${mode === 'inventory' ? 'tab-active' : ''}`}
          onClick={() => setMode('inventory')}
        >
          Inventory accounts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'external'}
          className={`tab ${mode === 'external' ? 'tab-active' : ''}`}
          onClick={() => setMode('external')}
        >
          Your own account
        </button>
      </div>

      {mode === 'external' ? (
        <ExternalBlock
          ext={ext}
          catalog={capCatalog}
          onField={(field, value) => setExt(prev => ({ ...prev, [field]: value }))}
          onAddDevice={() => setExt(prev => ({ ...prev, devices: [...prev.devices, emptyDevice()] }))}
          onRemoveDevice={di => setExt(prev => ({ ...prev, devices: prev.devices.filter((_, x) => x !== di) }))}
          onDevice={(di, field, value) => setExt(prev => ({ ...prev, devices: updateDeviceRow(prev.devices, di, field, value) }))}
        />
      ) : (
        <>
          {accounts.map((a, i) => (
            <AccountBlock
              key={a.id}
              index={i}
              account={a}
              catalog={capCatalog}
              canRemove={accounts.length > 1}
              onRemove={() => removeAccount(i)}
              onEnv={env => setEnv(i, env)}
              onType={type => setType(i, type)}
              onToggleFed={key => toggleFed(i, key)}
              onTogglePublicCharger={() => togglePublicCharger(i)}
              onToggleInternalRing={() => toggleInternalRing(i)}
              onAddDevice={() => addDevice(i)}
              onRemoveDevice={di => removeDevice(i, di)}
              onDevice={(di, field, value) => setDevice(i, di, field, value)}
            />
          ))}

          <button type="button" className="btn btn-sm btn-outline self-start" onClick={addAccount}>
            + Add account
          </button>
        </>
      )}

      {!loading && unmet.length > 0 && (
        <div role="alert" className="alert alert-warning alert-soft items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v3.75m0 3.75h.008M10.34 3.94l-7.6 13.16A1.5 1.5 0 0 0 4.04 19.5h15.92a1.5 1.5 0 0 0 1.3-2.4L13.66 3.94a1.5 1.5 0 0 0-2.6 0z" />
          </svg>
          <div>
            <p className="font-medium">Bundle Requirements:</p>
            <ul className="list-disc list-inside mt-0.5">
              {unmet.map(m => <li key={m}>{m}</li>)}
            </ul>
          </div>
        </div>
      )}

      <button type="submit" className="btn btn-primary" disabled={loading || unmet.length > 0} aria-busy={loading}>
        {loading
          ? <Spinner size="sm" />
          : mode === 'external'
            ? 'Reserve Bundle (your account)'
            : `Reserve Bundle (${accounts.length} account${accounts.length !== 1 ? 's' : ''})`}
      </button>
    </form>
  )
}

function BundleCard({ bundle, cancelling, onCancel }) {
  return (
    <div className="rounded-box border border-base-300 p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/bundles/${bundle.id}`} className="link link-hover font-mono text-xs opacity-60">
          {bundle.id.slice(0, 8)}…
        </Link>
        <span className={`badge badge-sm ${STATUS_BADGE[bundle.association_status] ?? 'badge-neutral'}`}>
          {bundle.association_status}
        </span>
        <span className="text-xs text-base-content/40 ml-auto">
          <Instant iso={bundle.created_at} style="compact" />
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {bundle.accounts.map(acct => (
          <div
            key={acct.account_id ?? `${acct.account_email}-${acct.environment}`}
            className="rounded-md border border-base-300/60 p-3 flex flex-col gap-2"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="badge badge-primary badge-sm">account</span>
              {!acct.account_id && (
                <span className="badge badge-sm badge-warning" title="Not a QA inventory account">
                  external
                </span>
              )}
              {acct.account_id ? (
                <Link to={`/accounts/${acct.account_id}`} className="link link-hover text-sm font-medium">
                  {acct.account_email}
                </Link>
              ) : (
                <span className="text-sm font-medium">{acct.account_email}</span>
              )}
              <span
                className={`badge badge-sm ${ACCOUNT_TYPE_COLORS[acct.account_type] ?? 'badge-ghost'} ml-auto`}
              >
                {ACCOUNT_TYPE_LABELS[acct.account_type] ?? acct.account_type}
              </span>
              <span className="badge badge-sm badge-outline">{acct.environment}</span>
            </div>
            {acct.device_mdis.length > 0 && (
              <div className="flex items-start gap-2">
                <span className="badge badge-secondary badge-sm mt-0.5 shrink-0 whitespace-nowrap">devices</span>
                <div className="flex flex-wrap gap-1">
                  {acct.device_mdis.map(mdi => (
                    <span key={mdi} className="font-mono text-xs bg-base-300/60 rounded px-1.5 py-0.5">{mdi}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {bundle.association_status === 'disassociation_failed' && (
        <p className="text-xs text-error">
          Reservations were released, but some devices could not be disassociated in Emporia Cloud.
          Retry cancel, or clean up manually.
        </p>
      )}

      <div>
        <button
          className="btn btn-sm btn-error btn-outline"
          disabled={cancelling === bundle.id}
          aria-busy={cancelling === bundle.id}
          onClick={() => onCancel(bundle)}
        >
          {cancelling === bundle.id
            ? <Spinner size="xs" />
            : bundle.association_status === 'disassociation_failed' ? 'Retry cancel' : 'Cancel bundle'}
        </button>
      </div>
    </div>
  )
}

export default function Bundles() {
  const [cancelling, setCancelling] = useState(null)
  const { show } = useToast()

  const { data, loading, reload } = useAsyncData(
    () => api.getBundles(),
    [],
    { onError: err => show(err.message, 'error') },
  )
  const bundles = data ?? []

  async function cancel(bundle) {
    setCancelling(bundle.id)
    try {
      const res = await api.cancelBundle(bundle.id)
      if (res.disassociation_failed) {
        show(`Reservations released, but ${res.failed_mdis.length} device(s) failed to disassociate`, 'error')
      } else {
        show('Bundle cancelled', 'success')
      }
      await reload()
    } catch (err) {
      show(err.message, 'error')
    } finally {
      setCancelling(null)
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <h1 className="text-2xl font-bold">Bundles</h1>
        <RefreshButton onClick={reload} />
      </div>

      <div className="flex flex-col gap-10">
        <section>
          <h2 className="text-base font-semibold mb-4">Create Bundle</h2>
          <CreateBundle onCreated={reload} />
        </section>

        <section>
          <h2 className="text-base font-semibold mb-4">My Bundles</h2>
          {loading ? (
            <PageSpinner />
          ) : bundles.length === 0 ? (
            <div className="rounded-box border border-base-300/60 py-10 text-center text-sm text-base-content/30">
              You have no active bundles
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {bundles.map(b => (
                <BundleCard key={b.id} bundle={b} cancelling={cancelling} onCancel={cancel} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
